import React, { useState } from "react";
import { Editor } from "@monaco-editor/react";
import {
  ChevronDown,
  Sparkles,
  Plus,
  X,
  ArrowRightLeft,
  FileText,
  Lightbulb,
  FlaskConical,
  Lock,
  Code2,
} from "lucide-react";

// ── Shared textarea style ─────────────────────────────────────────────────────
const ta = (error) =>
  `w-full bg-white dark:bg-white/[0.04] px-4 py-3 rounded-xl dark:text-white text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40 placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none border transition-colors duration-150 ${
    error
      ? "ring-2 ring-red-400 border-red-300 dark:border-red-500/40"
      : "border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/[0.14]"
  }`;

// ── Character progress bar ────────────────────────────────────────────────────
const CharCount = ({ value = "", max }) => {
  const len = value?.length ?? 0;
  const pct = Math.min((len / max) * 100, 100);
  const barColor =
    len > max * 0.9
      ? "bg-rose-400"
      : len > max * 0.75
      ? "bg-amber-400"
      : "bg-emerald-400";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-0.5 rounded-full bg-gray-200 dark:bg-white/[0.06] overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-200 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        className={`text-[11px] tabular-nums flex-shrink-0 ${
          len > max ? "text-rose-400" : "text-gray-400 dark:text-gray-500"
        }`}
      >
        {len}/{max}
      </span>
    </div>
  );
};

// ── Collapsible section card ──────────────────────────────────────────────────
const Section = ({ icon: Icon, title, badge, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-200 dark:border-white/[0.1] overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors duration-150"
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-blue-50 dark:bg-blue-500/10 flex-shrink-0">
            <Icon size={14} className="text-textHeading dark:text-blue-400" />
          </div>
          <span
            className="text-sm font-semibold dark:text-white text-gray-800"
            style={{ fontFamily: "Poppins, sans-serif" }}
          >
            {title}
          </span>
          {badge && (
            <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-500/20">
              <Sparkles size={9} />
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`text-gray-400 dark:text-gray-500 transition-transform duration-200 flex-shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 dark:border-white/[0.06] pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Language list for boilerplate ─────────────────────────────────────────────
const LANGUAGES = [
  { value: "javascript", label: "JavaScript" },
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "cpp", label: "C++" },
  { value: "c", label: "C" },
  { value: "go", label: "Go" },
  { value: "cs", label: "C#" },
  { value: "apex", label: "Apex" },
];

// Monaco's built-in language id for C# is "csharp", not our internal "cs" code.
const toMonacoLanguage = (lang) => (lang === "cs" ? "csharp" : lang);

// ── Error line ────────────────────────────────────────────────────────────────
const ErrLine = ({ msg }) =>
  msg ? (
    <p className="text-red-400 text-xs flex items-center gap-1">
      <span className="w-3 h-3 rounded-full bg-red-400 text-white text-[8px] flex items-center justify-center font-bold flex-shrink-0">
        !
      </span>
      {msg}
    </p>
  ) : null;

// ─────────────────────────────────────────────────────────────────────────────

const CodingProblemReviewForm = ({ formData, setFormData, errors }) => {
  const [boilerplateLang, setBoilerplateLang] = useState(
    Object.keys(formData.boilerplate_code ?? {})[0] ?? "javascript"
  );
  const [testTab, setTestTab] = useState("sample");

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const setField = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const setExample = (i, key, val) =>
    setFormData((p) => {
      const next = [...p.examples];
      next[i] = { ...next[i], [key]: val };
      return { ...p, examples: next };
    });

  const addExample = () =>
    setFormData((p) => ({ ...p, examples: [...p.examples, { input: "", output: "", explanation: "" }] }));

  const removeExample = (i) =>
    setFormData((p) => ({ ...p, examples: p.examples.filter((_, idx) => idx !== i) }));

  const setTestCase = (key, i, field, val) =>
    setFormData((p) => {
      const next = [...p[key]];
      next[i] = { ...next[i], [field]: val };
      return { ...p, [key]: next };
    });

  const addTestCase = (key) =>
    setFormData((p) => ({ ...p, [key]: [...p[key], { input: "", output: "" }] }));

  const removeTestCase = (key, i) =>
    setFormData((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));

  const setBoilerplate = (lang, code) =>
    setFormData((p) => ({ ...p, boilerplate_code: { ...p.boilerplate_code, [lang]: code } }));

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Title & Description ── */}
      <Section icon={FileText} title="Basic Information">
        {/* Title */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium dark:text-gray-200 text-gray-700">
            Problem Title <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setField("title", e.target.value)}
            maxLength={150}
            className={`w-full bg-white dark:bg-white/[0.04] px-4 py-3 rounded-xl dark:text-white text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40 border transition-colors duration-150 ${
              errors?.title
                ? "ring-2 ring-red-400 border-red-300 dark:border-red-500/40"
                : "border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/[0.14]"
            }`}
          />
          <div className="space-y-1 mt-1">
            <ErrLine msg={errors?.title} />
            <CharCount value={formData.title} max={150} />
          </div>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium dark:text-gray-200 text-gray-700">
            Description <span className="text-red-400">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setField("description", e.target.value)}
            rows={4}
            maxLength={2000}
            className={ta(errors?.description)}
          />
          <div className="space-y-1 mt-1">
            <ErrLine msg={errors?.description} />
            <CharCount value={formData.description} max={2000} />
          </div>
        </div>
      </Section>

      {/* ── Format & Constraints ── */}
      <Section icon={ArrowRightLeft} title="Format & Constraints" badge="AI generated">
        {[
          { field: "input_format", label: "Input Format", max: 500 },
          { field: "output_format", label: "Output Format", max: 500 },
          { field: "constraints", label: "Constraints", max: 500 },
        ].map(({ field, label, max }) => (
          <div key={field} className="space-y-1.5">
            <label className="block text-sm font-medium dark:text-gray-200 text-gray-700">
              {label} <span className="text-red-400">*</span>
            </label>
            <textarea
              value={formData[field]}
              onChange={(e) => setField(field, e.target.value)}
              rows={2}
              maxLength={max}
              className={ta(errors?.[field])}
            />
            <div className="space-y-1 mt-1">
              <ErrLine msg={errors?.[field]} />
              <CharCount value={formData[field]} max={max} />
            </div>
          </div>
        ))}
      </Section>

      {/* ── Examples ── */}
      <Section icon={Lightbulb} title="Examples" badge="AI generated">
        {formData.examples.map((ex, i) => (
          <div
            key={i}
            className="space-y-3 border border-gray-200 dark:border-white/[0.08] rounded-xl p-4 relative bg-gray-50/50 dark:bg-white/[0.02]"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500 dark:text-blue-400">
                Example {i + 1}
              </span>
              {formData.examples.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeExample(i)}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-all"
                >
                  <X size={11} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium dark:text-gray-300 text-gray-600">Input</label>
                <textarea value={ex.input} onChange={(e) => setExample(i, "input", e.target.value)} rows={3} className={ta(errors?.examples?.[i]?.input)} />
                <ErrLine msg={errors?.examples?.[i]?.input} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium dark:text-gray-300 text-gray-600">Output</label>
                <textarea value={ex.output} onChange={(e) => setExample(i, "output", e.target.value)} rows={3} className={ta(errors?.examples?.[i]?.output)} />
                <ErrLine msg={errors?.examples?.[i]?.output} />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium dark:text-gray-300 text-gray-600">Explanation</label>
              <textarea value={ex.explanation} onChange={(e) => setExample(i, "explanation", e.target.value)} rows={2} maxLength={2000} className={ta(errors?.examples?.[i]?.explanation)} />
              <div className="space-y-1 mt-1">
                <ErrLine msg={errors?.examples?.[i]?.explanation} />
                <CharCount value={ex.explanation} max={2000} />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addExample}
          className="w-full border-btn dark:border-btn-dark flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-all"
        >
          <Plus size={14} /> Add Example
        </button>
      </Section>

      {/* ── Test Cases ── */}
      <Section icon={FlaskConical} title="Test Cases" badge="AI generated">
        {/* tabs */}
        <div className="flex gap-1 p-1 dark:bg-white/5 bg-gray-100 rounded-xl">
          {[
            { key: "sample", label: "Sample", icon: FlaskConical, field: "test_cases" },
            { key: "final", label: "Final (Hidden)", icon: Lock, field: "final_test_cases" },
          ].map(({ key, label, icon: Icon, field }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTestTab(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                testTab === key
                  ? "bg-gradient-to-r from-pink-500/80 to-blue-500/80 text-white shadow-sm"
                  : "dark:text-gray-400 text-textSecondary hover:dark:text-white hover:text-textPrimary"
              }`}
            >
              <Icon size={11} />
              {label}
              <span className={`text-[10px] ${testTab === key ? "text-white/70" : "text-textSecondary"}`}>
                ({formData[field]?.length ?? 0})
              </span>
            </button>
          ))}
        </div>

        {/* test case list */}
        {(testTab === "sample" ? formData.test_cases : formData.final_test_cases).map((tc, i) => {
          const stateKey = testTab === "sample" ? "test_cases" : "final_test_cases";
          const errSet = testTab === "sample" ? errors?.test_cases : errors?.final_test_cases;
          return (
            <div
              key={i}
              className="border border-gray-200 dark:border-white/[0.08] rounded-xl p-4 space-y-2 bg-gray-50/50 dark:bg-white/[0.02]"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500 dark:text-blue-400">
                  Case {i + 1}
                </span>
                {formData[stateKey].length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTestCase(stateKey, i)}
                    className="w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-all"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium dark:text-gray-300 text-gray-600">Input</label>
                  <textarea value={tc.input} onChange={(e) => setTestCase(stateKey, i, "input", e.target.value)} rows={3} className={ta(errSet?.[i]?.input)} />
                  <ErrLine msg={errSet?.[i]?.input} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium dark:text-gray-300 text-gray-600">Expected Output</label>
                  <textarea value={tc.output} onChange={(e) => setTestCase(stateKey, i, "output", e.target.value)} rows={3} className={ta(errSet?.[i]?.output)} />
                  <ErrLine msg={errSet?.[i]?.output} />
                </div>
              </div>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => addTestCase(testTab === "sample" ? "test_cases" : "final_test_cases")}
          className="w-full border-btn dark:border-btn-dark flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-all"
        >
          <Plus size={14} /> Add Test Case
        </button>
      </Section>

      {/* ── Boilerplate Code ── */}
      <Section icon={Code2} title="Boilerplate Code" badge="AI generated" defaultOpen={false}>
        <p className="text-xs dark:text-gray-400 text-gray-500">
          Starter code candidates will see. Edit or leave as-is.
        </p>
        <div className="flex flex-wrap gap-2">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.value}
              type="button"
              onClick={() => setBoilerplateLang(lang.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                boilerplateLang === lang.value
                  ? "bg-gradient-to-r from-pink-500/80 to-blue-500/80 text-white shadow-sm"
                  : "dark:bg-white/5 bg-gray-100 dark:text-gray-300 text-gray-600 border border-gray-200 dark:border-white/[0.08] hover:dark:bg-white/10 hover:border-gray-300"
              }`}
            >
              {lang.label}
              {formData.boilerplate_code?.[lang.value] && (
                <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              )}
            </button>
          ))}
        </div>
        <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-between px-4 py-2 dark:bg-gray-800 bg-gray-700">
            <span className="text-xs font-mono text-gray-400">
              {LANGUAGES.find((l) => l.value === boilerplateLang)?.label}
            </span>
            <span className="text-[10px] text-gray-500">boilerplate</span>
          </div>
          <Editor
            theme="vs-dark"
            height="260px"
            language={toMonacoLanguage(boilerplateLang)}
            value={formData.boilerplate_code?.[boilerplateLang] ?? ""}
            onChange={(val) => setBoilerplate(boilerplateLang, val ?? "")}
            options={{ fontSize: 13, minimap: { enabled: false }, scrollBeyondLastLine: false, padding: { top: 10, bottom: 10 } }}
          />
        </div>
      </Section>
    </div>
  );
};

export default CodingProblemReviewForm;
